import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';
import { DetailsTab } from '../components/match/DetailsTab';
import { SquadTab } from '../components/match/SquadTab';
import { StatsTab } from '../components/match/StatsTab';
import { EventsTab } from '../components/match/EventsTab';
import { CoachNotesTab } from '../components/match/CoachNotesTab';
import { DuesTab } from '../components/match/DuesTab';
import { AddEventForm } from '../components/match/AddEventForm';
import { 
  FORMATIONS, 
  FORMATIONS_BY_FORMAT,
  MATCH_FORMATS,
  FORMATION_POSITIONS 
} from '../components/match/constants';
import { 
  ArrowLeft, 
  Calendar, 
  Users, 
  Clock, 
  Target, 
  Trash2,
  Timer,
  NotebookPen,
  Coins,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveBadgeUrl = (badge) => {
  if (!badge) return null;
  if (badge.startsWith('http://') || badge.startsWith('https://')) return badge;
  return `${BACKEND_URL}${badge}`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'details', label: 'Details', icon: Calendar },
  { id: 'squad', label: 'Squad', icon: Users },
  { id: 'events', label: 'Events', icon: Clock },
  { id: 'stats', label: 'Stats', icon: Target },
  { id: 'dues', label: 'Dues', icon: Coins },
  { id: 'notes', label: 'Notes', icon: NotebookPen },
];

export default function MatchEdit() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const isNewMatch = !matchId || matchId === 'new';
  const isViewOnly = !isAdmin;
  
  const initialTab = searchParams.get('tab') || 'details';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Match details state
  const [match, setMatch] = useState(null);
  const [formData, setFormData] = useState({
    opponent: '', date: '', kick_off_time: '', venue: 'home', competition: '',
    status: 'upcoming', our_score: '', opponent_score: '', player_dues: '',
    is_cup: false, cup_round: '', cup_group_result: '', our_score_et: '', opponent_score_et: '', our_score_pens: '', opponent_score_pens: '',
    half_length: '', extra_time_half_length: '', match_type: '',
    coach_notes: [], coach_notes_visibility: 'private'
  });
  
  // Check if this match is today (for match day mode button)
  const isMatchDay = !isNewMatch && formData.date === new Date().toISOString().split('T')[0];
  const matchDayModeEnabled = user?.match_day_mode_enabled !== false;
  
  const [savedOpponents, setSavedOpponents] = useState([]);
  const [savedCompetitions, setSavedCompetitions] = useState([]);
  const [opponentBadge, setOpponentBadge] = useState(null);
  // Live-resolve opponent badge as the form's opponent value changes (covers
  // free-text typing in DetailsTab + initial load when the formData reseats).
  useEffect(() => {
    if (!formData.opponent || user?.show_opponent_badges === false) {
      setOpponentBadge(null);
      return;
    }
    const target = formData.opponent.toLowerCase().trim();
    const found = (savedOpponents || []).find(o => o?.name?.toLowerCase().trim() === target);
    setOpponentBadge(resolveBadgeUrl(found?.badge));
  }, [formData.opponent, savedOpponents, user?.show_opponent_badges]);
  
  // Squad state
  const [players, setPlayers] = useState([]);
  const [matchFormat, setMatchFormat] = useState('11v11');
  const [benchSlots, setBenchSlots] = useState(7);
  const [formation, setFormation] = useState('4-4-2');
  const [lineup, setLineup] = useState([]);
  const [subs, setSubs] = useState([]);
  // Phase 1.75: metadata about the last screenshot import so the SquadTab
  // can surface "N imported · M need review" + list unmatched detected
  // names. Cleared once the coach dismisses the banner.
  const [importedMeta, setImportedMeta] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [draggedPlayer, setDraggedPlayer] = useState(null);
  const [draggedPosition, setDraggedPosition] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);
  
  // Shirt number state
  const [assignedNumbers, setAssignedNumbers] = useState({});
  const [numberSelectOpen, setNumberSelectOpen] = useState(false);
  const [numberSelectPosition, setNumberSelectPosition] = useState(null);
  const [pendingPlayerSelect, setPendingPlayerSelect] = useState(null);
  const [selectedBenchPosition, setSelectedBenchPosition] = useState(null);
  const [subNumbers, setSubNumbers] = useState({});
  const [captainId, setCaptainId] = useState(null);
  const [nonSelectionReasons, setNonSelectionReasons] = useState({});
  
  const availableFormations = useMemo(() => {
    return FORMATIONS_BY_FORMAT[matchFormat] || FORMATIONS;
  }, [matchFormat]);
  
  const handleFormatChange = (newFormat) => {
    setMatchFormat(newFormat);
    const formatConfig = MATCH_FORMATS.find(f => f.value === newFormat);
    if (formatConfig) {
      setBenchSlots(formatConfig.defaultBenchSlots);
      const newFormations = FORMATIONS_BY_FORMAT[newFormat] || FORMATIONS;
      if (!newFormations.includes(formation)) {
        setFormation(newFormations[0]);
        setLineup([]);
        setAssignedNumbers({});
      }
    }
  };
  
  const pitchRef = useRef(null);
  const playerListRef = useRef(null);
  
  // Events state
  const [events, setEvents] = useState([]);
  const [addEventOpen, setAddEventOpen] = useState(false);
  
  // Stats state
  const [playerStats, setPlayerStats] = useState([]);
  
  // Initial states for tracking changes
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialSquadData, setInitialSquadData] = useState(null);
  const [initialEvents, setInitialEvents] = useState([]);
  const [initialStats, setInitialStats] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!initialFormData) return false;
    const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    const currentSquad = { 
      match_format: matchFormat, bench_slots: benchSlots, formation, lineup, 
      subs: subs.filter(s => s && s.playerId), 
      lineup_numbers: assignedNumbers, sub_numbers: subNumbers 
    };
    const squadChanged = initialSquadData && (JSON.stringify(currentSquad) !== JSON.stringify(initialSquadData));
    const eventsChanged = JSON.stringify(events) !== JSON.stringify(initialEvents);
    const statsChanged = JSON.stringify(playerStats) !== JSON.stringify(initialStats);
    return formChanged || squadChanged || eventsChanged || statsChanged;
  }, [formData, initialFormData, matchFormat, benchSlots, formation, lineup, subs, assignedNumbers, subNumbers, initialSquadData, events, initialEvents, playerStats, initialStats]);

  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChanges(hasUnsavedChanges);

  useEffect(() => { fetchData(); }, [matchId]);

  const fetchData = async () => {
    try {
      const [playersRes, opponentsRes, competitionsRes] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/opponents`).catch(() => ({ data: [] })),
        axios.get(`${API}/competitions`).catch(() => ({ data: [] }))
      ]);
      setPlayers(playersRes.data);
      setSavedOpponents(opponentsRes.data);
      setSavedCompetitions(competitionsRes.data);

      if (!isNewMatch) {
        const matchRes = await axios.get(`${API}/matches/${matchId}`);
        const matchData = matchRes.data;
        setMatch(matchData);
        
        const formDataFromMatch = {
          opponent: matchData.opponent || '', date: matchData.date || '',
          kick_off_time: matchData.kick_off_time || '', venue: matchData.venue || 'home',
          competition: matchData.competition || '', status: matchData.status || 'upcoming',
          our_score: matchData.our_score?.toString() || '', opponent_score: matchData.opponent_score?.toString() || '',
          player_dues: matchData.player_dues?.toString() || '', is_cup: matchData.is_cup || false,
          cup_round: matchData.cup_round || '', cup_group_result: matchData.cup_group_result || '',
          our_score_et: matchData.our_score_et?.toString() || '', opponent_score_et: matchData.opponent_score_et?.toString() || '',
          our_score_pens: matchData.our_score_pens?.toString() || '', opponent_score_pens: matchData.opponent_score_pens?.toString() || '',
          half_length: matchData.half_length?.toString() || '', extra_time_half_length: matchData.extra_time_half_length?.toString() || '',
          match_type: matchData.match_type || '',
          coach_notes: matchData.coach_notes || [],
          coach_notes_visibility: matchData.coach_notes_visibility || 'private'
        };
        setFormData(formDataFromMatch);
        setInitialFormData(formDataFromMatch);
        
        setMatchFormat(matchData.match_format || '11v11');
        setBenchSlots(matchData.bench_slots || 7);
        setFormation(matchData.formation || '4-4-2');
        setLineup(matchData.lineup || []);
        setSubs(matchData.subs || []);
        setAssignedNumbers(matchData.lineup_numbers || {});
        setSubNumbers(matchData.sub_numbers || {});
        setCaptainId(matchData.captain_id || null);
        setNonSelectionReasons(matchData.non_selection_reasons || {});
        setInitialSquadData({
          match_format: matchData.match_format || '11v11', bench_slots: matchData.bench_slots || 7,
          formation: matchData.formation || '4-4-2', lineup: matchData.lineup || [],
          subs: (matchData.subs || []).filter(s => s && s.playerId),
          lineup_numbers: matchData.lineup_numbers || {},
          sub_numbers: matchData.sub_numbers || {}
        });
        
        setEvents(matchData.events || []);
        setInitialEvents(matchData.events || []);
        
        const existingStats = matchData.player_stats || [];
        const subPlayerIds = (matchData.subs || []).filter(sub => sub != null).map(sub => typeof sub === 'object' && sub.playerId ? sub.playerId : sub);
        const allPlayerIds = [...(matchData.lineup || []), ...subPlayerIds].filter(id => id != null);
        
        const stats = allPlayerIds.map(playerId => {
          const existing = existingStats.find(s => s.player_id === playerId);
          const isStarter = (matchData.lineup || []).includes(playerId);
          return {
            player_id: playerId, goals: existing?.goals || 0, assists: existing?.assists || 0,
            yellow_cards: existing?.yellow_cards || 0, red_cards: existing?.red_cards || 0,
            minutes_played: existing?.minutes_played || (isStarter ? 90 : 0),
            is_starter: isStarter, is_pom: existing?.is_pom ?? false,
            dues_paid: existing?.dues_paid ?? existing?.paid ?? false
          };
        });
        setPlayerStats(stats);
        setInitialStats(stats);
      } else {
        if (user?.preferred_formation) setFormation(user.preferred_formation);
        const newFormData = { ...formData };
        if (user?.default_kick_off_time) newFormData.kick_off_time = user.default_kick_off_time;
        if (user?.half_length) newFormData.half_length = user.half_length.toString();
        if (user?.extra_time_half_length) newFormData.extra_time_half_length = user.extra_time_half_length.toString();
        setFormData(newFormData);
        setInitialFormData(newFormData);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load match data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.opponent || !formData.date) {
      toast.error('Please fill in opponent and date');
      setActiveTab('details');
      return;
    }
    setSaving(true);
    try {
      const matchPayload = {
        opponent: formData.opponent, date: formData.date, kick_off_time: formData.kick_off_time || '',
        venue: formData.venue, competition: formData.competition || '', status: formData.status,
        our_score: formData.our_score ? parseInt(formData.our_score) : null,
        opponent_score: formData.opponent_score ? parseInt(formData.opponent_score) : null,
        player_dues: formData.player_dues ? parseFloat(formData.player_dues) : 0,
        is_cup: formData.is_cup || false,
        cup_round: formData.is_cup ? (formData.cup_round || null) : null,
        cup_group_result: formData.is_cup && formData.cup_round === 'group_stage' ? (formData.cup_group_result || null) : null,
        our_score_et: formData.our_score_et ? parseInt(formData.our_score_et) : null,
        opponent_score_et: formData.opponent_score_et ? parseInt(formData.opponent_score_et) : null,
        our_score_pens: formData.our_score_pens ? parseInt(formData.our_score_pens) : null,
        opponent_score_pens: formData.opponent_score_pens ? parseInt(formData.opponent_score_pens) : null,
        match_format: matchFormat, bench_slots: benchSlots, formation, lineup,
        lineup_numbers: assignedNumbers, subs, sub_numbers: subNumbers,
        captain_id: captainId, events, player_stats: playerStats,
        non_selection_reasons: nonSelectionReasons,
        half_length: formData.half_length ? parseInt(formData.half_length) : null,
        extra_time_half_length: formData.extra_time_half_length ? parseInt(formData.extra_time_half_length) : null,
        match_type: formData.match_type || null,
        coach_notes: formData.coach_notes || [],
        coach_notes_visibility: formData.coach_notes_visibility || 'private'
      };

      if (isNewMatch) {
        const response = await axios.post(`${API}/matches`, matchPayload);
        toast.success('Match created successfully');
        navigate(`/matches/${response.data.id}/edit`);
      } else {
        await axios.put(`${API}/matches/${matchId}`, matchPayload);
        toast.success('Match saved successfully');
        setInitialFormData(formData);
        setInitialSquadData({
          match_format: matchFormat,
          bench_slots: benchSlots,
          formation,
          lineup,
          subs: subs.filter(s => s && s.playerId),
          lineup_numbers: assignedNumbers,
          sub_numbers: subNumbers,
        });
        setInitialEvents(events);
        setInitialStats(playerStats);
      }
    } catch (error) {
      console.error('Failed to save match:', error);
      toast.error('Failed to save match');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this match?')) return;
    try {
      await axios.delete(`${API}/matches/${matchId}`);
      toast.success('Match deleted');
      navigate('/matches');
    } catch (error) {
      toast.error('Failed to delete match');
    }
  };

  // Squad helpers
  const positions = FORMATION_POSITIONS[formation] || FORMATION_POSITIONS['4-4-2'];
  
  const availablePlayers = useMemo(() => {
    const subPlayerIds = subs.filter(s => s && s.playerId).map(s => s.playerId);
    const usedIds = [...lineup, ...subPlayerIds].filter(Boolean);
    return players
      .filter(p => !usedIds.includes(p.id))
      .filter(p => (p.status || 'available') === 'available')
      .filter(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [players, lineup, subs, searchQuery]);

  const handlePositionClick = (positionIndex) => {
    if (lineup[positionIndex]) {
      const removedPlayerId = lineup[positionIndex];
      const newLineup = [...lineup];
      newLineup[positionIndex] = null;
      setLineup(newLineup);
      setAssignedNumbers(prev => { const updated = { ...prev }; delete updated[positionIndex]; return updated; });
      if (removedPlayerId === captainId) setCaptainId(null);
    } else {
      setSelectedPosition(positionIndex);
      setSelectedBenchPosition(null);
      setTimeout(() => { playerListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    }
  };

  const handlePlayerDoubleClick = (positionIndex) => {
    if (lineup[positionIndex]) {
      setNumberSelectPosition(positionIndex);
      setNumberSelectOpen(true);
    }
  };

  const handleDragStart = (e, positionIndex) => {
    if (!lineup[positionIndex]) return;
    setDraggedPosition(positionIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', positionIndex.toString());
  };

  const handleDragOver = (e, positionIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedPosition !== null && draggedPosition !== positionIndex) setDragOverPosition(positionIndex);
  };

  const handleDragLeave = () => { setDragOverPosition(null); };

  const handleDrop = (e, targetPosition) => {
    e.preventDefault();
    if (draggedPosition === null || draggedPosition === targetPosition) {
      setDraggedPosition(null); setDragOverPosition(null); return;
    }
    const newLineup = [...lineup];
    const newAssignedNumbers = { ...assignedNumbers };
    const draggedPlayerId = newLineup[draggedPosition];
    const targetPlayerId = newLineup[targetPosition];
    newLineup[targetPosition] = draggedPlayerId;
    newLineup[draggedPosition] = targetPlayerId;
    const draggedNumber = newAssignedNumbers[draggedPosition];
    const targetNumber = newAssignedNumbers[targetPosition];
    if (draggedNumber !== undefined) newAssignedNumbers[targetPosition] = draggedNumber; else delete newAssignedNumbers[targetPosition];
    if (targetNumber !== undefined) newAssignedNumbers[draggedPosition] = targetNumber; else delete newAssignedNumbers[draggedPosition];
    setLineup(newLineup);
    setAssignedNumbers(newAssignedNumbers);
    setDraggedPosition(null);
    setDragOverPosition(null);
  };

  const handleDragEnd = () => { setDraggedPosition(null); setDragOverPosition(null); };

  const handleNumberSelect = (number) => {
    if (pendingPlayerSelect) {
      const { playerId, positionIndex, isSub } = pendingPlayerSelect;
      if (isSub) {
        const newSubs = [...subs];
        newSubs[positionIndex] = { playerId, number };
        setSubs(newSubs);
        setSubNumbers(prev => ({ ...prev, [positionIndex]: number }));
      } else {
        const newLineup = [...lineup];
        newLineup[positionIndex] = playerId;
        setLineup(newLineup);
        setAssignedNumbers(prev => ({ ...prev, [positionIndex]: number }));
      }
      setNumberSelectOpen(false); setNumberSelectPosition(null);
      setPendingPlayerSelect(null); setSelectedPosition(null); setSelectedBenchPosition(null);
      setTimeout(() => { pitchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    } else if (numberSelectPosition !== null) {
      setAssignedNumbers(prev => ({ ...prev, [numberSelectPosition]: number }));
      setNumberSelectOpen(false); setNumberSelectPosition(null);
    }
  };

  const getAvailableNumbers = () => {
    const lineupUsed = Object.values(assignedNumbers).map(n => parseInt(n)).filter(n => !isNaN(n));
    const subsUsed = [];
    subs.forEach((sub, idx) => {
      if (sub && sub.playerId) {
        const fromSubNumbers = subNumbers[idx] || subNumbers[String(idx)];
        if (fromSubNumbers) subsUsed.push(parseInt(fromSubNumbers));
        else if (sub.number) subsUsed.push(parseInt(sub.number));
      }
    });
    const usedNumbers = [...lineupUsed, ...subsUsed].filter(n => !isNaN(n));
    return Array.from({ length: 22 }, (_, i) => i + 1).filter(n => !usedNumbers.includes(n));
  };

  const handleBenchPositionClick = (benchIndex) => {
    const sub = subs[benchIndex];
    if (sub && sub.playerId) {
      const newSubs = [...subs]; newSubs[benchIndex] = null; setSubs(newSubs);
      setSubNumbers(prev => { const updated = { ...prev }; delete updated[benchIndex]; return updated; });
    } else {
      setSelectedBenchPosition(benchIndex); setSelectedPosition(null);
      setTimeout(() => { playerListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    }
  };

  const handleBenchDoubleClick = (benchIndex) => {
    if (subs[benchIndex]?.playerId) {
      setPendingPlayerSelect({ playerId: subs[benchIndex].playerId, positionIndex: benchIndex, isSub: true });
      setNumberSelectOpen(true);
    }
  };

  const handlePlayerSelect = (playerId) => {
    if (selectedPosition !== null) {
      setPendingPlayerSelect({ playerId, positionIndex: selectedPosition, isSub: false });
      setNumberSelectPosition(selectedPosition);
      setTimeout(() => { pitchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); setTimeout(() => { setNumberSelectOpen(true); }, 300); }, 100);
    } else if (selectedBenchPosition !== null) {
      setPendingPlayerSelect({ playerId, positionIndex: selectedBenchPosition, isSub: true });
      setTimeout(() => { pitchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); setTimeout(() => { setNumberSelectOpen(true); }, 300); }, 100);
    }
  };

  const handleAddSub = (playerId) => {
    const emptyIndex = subs.findIndex(s => !s || !s.playerId);
    if (emptyIndex !== -1) {
      setSelectedBenchPosition(emptyIndex);
      setPendingPlayerSelect({ playerId, positionIndex: emptyIndex, isSub: true });
      setNumberSelectOpen(true);
    } else if (subs.length < 9) {
      setSelectedBenchPosition(subs.length);
      setPendingPlayerSelect({ playerId, positionIndex: subs.length, isSub: true });
      setNumberSelectOpen(true);
    }
  };

  const handleRemoveSub = (index) => {
    const newSubs = [...subs]; newSubs[index] = null; setSubs(newSubs);
    setSubNumbers(prev => { const updated = { ...prev }; delete updated[index]; return updated; });
  };

  // Events
  const handleAddEvent = (newEvents) => {
    const eventsToAdd = Array.isArray(newEvents) ? newEvents : [newEvents];
    setEvents([...events, ...eventsToAdd].sort((a, b) => a.minute - b.minute));
  };

  const handleRemoveEvent = (index) => { setEvents(events.filter((_, i) => i !== index)); };

  // Apply a Lineup Screenshot Import result to lineup + subs + events.
  // - Starters fill the lineup array in order; unmatched (no player_id)
  //   leave that slot null so the coach can pick manually (safe default).
  // - Subs bench mirrors the same shape used by SquadTab ({playerId}).
  // - Events convert into MatchEvent shape ('goal', 'yellow_card',
  //   'red_card', 'substitution_out' + 'substitution_in' pair for subs)
  //   with best-guess minute + resolved player_id, dropping any where
  //   the fuzzy matcher couldn't identify the player.
  const handleImportedFullMatch = ({ starters, subs_bench, events: parsedEvents }) => {
    if (Array.isArray(starters) && starters.length) {
      // Preserve current lineup length (e.g. 11 for an 11-a-side match);
      // pad or truncate starter IDs to match.
      const target = lineup.length > 0 ? lineup.length : Math.max(starters.length, 11);
      const next = Array.from({ length: target }, (_, i) => starters[i]?.player_id || null);
      setLineup(next);

      // Track detected jersey numbers so LineupTab can render them.
      const numbers = { ...assignedNumbers };
      starters.forEach((s) => {
        if (s.player_id && s.number != null) numbers[s.player_id] = String(s.number);
      });
      setAssignedNumbers(numbers);
    }

    if (Array.isArray(subs_bench) && subs_bench.length) {
      const nextSubs = subs_bench
        .filter((s) => s.player_id)
        .map((s) => ({ playerId: s.player_id }));
      setSubs(nextSubs);
    }

    if (Array.isArray(parsedEvents) && parsedEvents.length) {
      const newEvents = [];
      parsedEvents.forEach((e) => {
        if (e.type === 'substitution') {
          if (e.sub_off_id) newEvents.push({ event_type: 'substitution_out', player_id: e.sub_off_id, minute: e.minute });
          if (e.sub_on_id)  newEvents.push({ event_type: 'substitution_in',  player_id: e.sub_on_id,  minute: e.minute });
        } else if (e.player_id) {
          newEvents.push({ event_type: e.type, player_id: e.player_id, minute: e.minute, ...(e.assist_id ? { assist_player_id: e.assist_id } : {}) });
        }
      });
      if (newEvents.length) {
        setEvents([...events, ...newEvents].sort((a, b) => (a.minute || 0) - (b.minute || 0)));
      }
    }

    // Store import metadata for the SquadTab banner + amber review list.
    const unmatchedStarters = (starters || []).filter((s) => !s.player_id);
    const unmatchedSubs = (subs_bench || []).filter((s) => !s.player_id);
    setImportedMeta({
      imported_at: new Date().toISOString(),
      total_starters: starters?.length || 0,
      total_subs: subs_bench?.length || 0,
      total_events: parsedEvents?.length || 0,
      unmatched_starters: unmatchedStarters.map((p) => ({ name: p.detected_name, number: p.number, candidates: p.candidates || [] })),
      unmatched_subs: unmatchedSubs.map((p) => ({ name: p.detected_name, number: p.number, candidates: p.candidates || [] })),
    });
  };

  // Stats
  const handleStatChange = (playerId, field, value) => {
    setPlayerStats(playerStats.map(stat => stat.player_id === playerId ? { ...stat, [field]: value } : stat));
  };

  const squadCount = lineup.filter(Boolean).length + subs.filter(s => s && s.playerId).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  // Assemble squad state + handlers for SquadTab
  const squadState = {
    matchFormat, benchSlots, formation, lineup, subs,
    assignedNumbers, subNumbers, captainId,
    selectedPosition, selectedBenchPosition,
    draggedPosition, dragOverPosition,
    numberSelectOpen, numberSelectPosition, pendingPlayerSelect,
    availablePlayers, availableFormations, searchQuery,
    nonSelectionReasons
  };

  const squadHandlers = {
    handleFormatChange, setFormation, setBenchSlots,
    handlePositionClick, handlePlayerDoubleClick,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    handleNumberSelect, getAvailableNumbers,
    handleBenchPositionClick, handleBenchDoubleClick, handleRemoveSub,
    setCaptainId, handlePlayerSelect, handleAddSub,
    setSearchQuery, setNonSelectionReasons,
    setNumberSelectOpen, setNumberSelectPosition, setPendingPlayerSelect, setSelectedBenchPosition
  };

  return (
    <Layout>
      <UnsavedChangesDialog open={showPrompt} onConfirm={confirmNavigation} onCancel={cancelNavigation} />
      
      <div className="space-y-6 page-slide-in" data-testid="match-edit-container">
        {/* ── Broadcast Scoreboard Header (locked design 2026-05-23) ── */}
        {(() => {
          const venueLabel = (formData.venue || 'home').toUpperCase();
          const showBadges = user?.show_opponent_badges !== false;
          const clubBadge  = resolveBadgeUrl(user?.club_badge);
          const ourTeamName = user?.team_name || 'Our Team';
          const oppName = formData.opponent || (isNewMatch ? 'New Match' : 'Opponent');
          const matchTypeChip = (formData.match_type || '').replace(/_/g, ' ').toUpperCase();
          const hasResult = formData.status === 'result';
          const isPostponed = formData.status === 'postponed';
          const ourScoreDisplay  = hasResult ? (formData.our_score || '0') : '–';
          const oppScoreDisplay  = hasResult ? (formData.opponent_score || '0') : '–';

          // Status chip — mirrors the MatchDayMode period-chip pattern but for
          // pre/post-game contexts. Tone drives the border + text colour.
          const statusChip = (() => {
            if (isNewMatch) return { text: 'NEW MATCH', tone: 'gray' };
            if (isPostponed) return { text: 'POSTPONED', tone: 'amber' };
            if (isMatchDay && !hasResult) return { text: 'MATCH DAY', tone: 'emerald' };
            if (hasResult) {
              const ourPens = formData.our_score_pens;
              const oppPens = formData.opponent_score_pens;
              if (ourPens !== '' && ourPens !== undefined && ourPens !== null) {
                return { text: `FT · PEN ${ourPens}-${oppPens}`, tone: 'red' };
              }
              const ourEt = formData.our_score_et;
              if (ourEt !== '' && ourEt !== undefined && ourEt !== null) {
                return { text: 'FT · AET', tone: 'gray' };
              }
              return { text: 'FT', tone: 'gray' };
            }
            const koTime = formData.kick_off_time || '';
            return { text: koTime ? `UPCOMING · ${koTime}` : 'UPCOMING', tone: 'emerald' };
          })();
          const STATUS_CLASSES = {
            amber:   'border-amber-400/70 text-amber-300',
            emerald: 'border-emerald-400/60 text-emerald-300',
            red:     'border-red-400/60 text-red-300',
            gray:    'border-gray-400/50 text-gray-300',
          };

          return (
            <div className="rounded-2xl bg-gradient-to-br from-gray-950 via-gray-900 to-emerald-950 border border-gray-800 shadow-lg overflow-hidden" data-testid="matchedit-scoreboard">
              {/* Top bar: back · status pill + venue · save/delete */}
              <div className="flex items-center px-3 py-2.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/matches')}
                  className="text-gray-300 hover:text-white hover:bg-white/10 shrink-0 rounded-md"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                </Button>
                <div className="flex-1 flex items-center justify-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full border-2 text-xs font-bold uppercase tracking-[0.18em] bg-black/30 ${STATUS_CLASSES[statusChip.tone]}`}
                    data-testid="matchedit-status-chip"
                  >
                    {statusChip.text}
                  </span>
                  {!isNewMatch && (
                    <span className="text-sm font-bold uppercase tracking-[0.2em] text-white" data-testid="matchedit-venue-label">
                      {venueLabel}
                    </span>
                  )}
                </div>
                {!isViewOnly ? (
                  <div className="flex gap-2 shrink-0">
                    {!isNewMatch && (
                      <button onClick={handleDelete} className="px-3 py-1.5 rounded-lg bg-red-600/90 hover:bg-red-500 text-white text-sm font-bold flex items-center gap-1" data-testid="delete-match-btn">
                        <Trash2 className="w-4 h-4 sm:mr-0" strokeWidth={1.5} />
                        <span className="hidden sm:inline">Delete</span>
                      </button>
                    )}
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold disabled:opacity-50"
                      data-testid="save-match-btn"
                    >
                      {saving ? '...' : 'Save'}
                    </button>
                  </div>
                ) : (
                  <div className="w-9 shrink-0" /> /* balance the layout when read-only */
                )}
              </div>

              {/* Score row: optional badges flanking the score */}
              <div className="px-4 pb-3 pt-1 text-center" data-testid="matchedit-score">
                <div className="flex items-center justify-center gap-4 sm:gap-6">
                  {showBadges && (
                    clubBadge ? (
                      <img src={clubBadge} alt={`${ourTeamName} badge`} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-contain shrink-0 bg-white/5 p-1" />
                    ) : (
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center shrink-0">
                        <Shield className="w-7 h-7 text-white/30" strokeWidth={1.5} />
                      </div>
                    )
                  )}
                  <div className="flex items-center gap-4 sm:gap-6 font-mono">
                    <span className="text-5xl sm:text-6xl font-bold text-white leading-none tabular-nums" data-testid="matchedit-our-score">{ourScoreDisplay}</span>
                    <span className="text-3xl sm:text-4xl text-gray-500 leading-none">—</span>
                    <span className="text-5xl sm:text-6xl font-bold text-white leading-none tabular-nums" data-testid="matchedit-opp-score">{oppScoreDisplay}</span>
                  </div>
                  {showBadges && (
                    opponentBadge ? (
                      <img src={opponentBadge} alt={`${oppName} badge`} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-contain shrink-0 bg-white/5 p-1" />
                    ) : (
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center shrink-0">
                        <Shield className="w-7 h-7 text-white/30" strokeWidth={1.5} />
                      </div>
                    )
                  )}
                </div>
                <p className="text-sm text-gray-300 mt-3 truncate" data-testid="matchedit-fixture">
                  {ourTeamName} vs {oppName}
                </p>
                <div className="mt-1 flex items-center justify-center gap-2 flex-wrap">
                  {!isNewMatch && match && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                      <Calendar className="w-3 h-3" strokeWidth={2} />
                      {formatDateUK(match.date)}
                    </span>
                  )}
                  {matchTypeChip && (
                    <span
                      className="inline-block px-2 py-0.5 rounded bg-gray-900 text-white text-[10px] font-bold uppercase tracking-[0.2em]"
                      data-testid="matchedit-type-chip"
                    >
                      {matchTypeChip}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Tab Navigation */}
        <div className="bg-gray-100 rounded-2xl p-1.5 flex gap-1" data-testid="tab-navigation">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const count = tab.id === 'squad' ? squadCount : null;
            const isPostponed = formData.status === 'postponed';
            if (isViewOnly && tab.id === 'details') return null;
            if (tab.id === 'notes' && isViewOnly) {
              const publicCount = (formData.coach_notes || []).filter(n => (n.visibility || 'private') === 'public').length;
              if (publicCount === 0) return null;
            }
            if (tab.id === 'notes' && isNewMatch) return null;
            const isDisabled = 
              (isNewMatch && (tab.id === 'events' || tab.id === 'stats' || tab.id === 'dues')) ||
              (isPostponed && (tab.id === 'squad' || tab.id === 'events' || tab.id === 'stats' || tab.id === 'dues'));
            
            return (
              <button
                key={tab.id}
                onClick={() => !isDisabled && setActiveTab(tab.id)}
                disabled={isDisabled}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm transition-all ${
                  isActive ? 'bg-white text-gray-900 shadow-sm' 
                    : isDisabled ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">{tab.label}</span>
                {count !== null && count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'details' && !isViewOnly && (
            <DetailsTab
              formData={formData}
              onFormChange={setFormData}
              savedOpponents={savedOpponents}
              savedCompetitions={savedCompetitions}
              isNewMatch={isNewMatch}
              onImportedFullMatch={handleImportedFullMatch}
            />
          )}

          {activeTab === 'squad' && (
            <SquadTab
              squadState={squadState}
              squadHandlers={squadHandlers}
              players={players}
              isViewOnly={isViewOnly}
              user={user}
              refs={{ pitchRef, playerListRef }}
              importedMeta={importedMeta}
              onDismissImportedMeta={() => setImportedMeta(null)}
            />
          )}

          {activeTab === 'events' && !isNewMatch && (
            <>
              <EventsTab
                events={events}
                players={players}
                isViewOnly={isViewOnly}
                onAddEvent={() => setAddEventOpen(true)}
                onRemoveEvent={handleRemoveEvent}
                buttonColor={user?.button_color || '#064E3B'}
                teamName={user?.team_name || 'Our Team'}
                opponentName={formData.opponent || 'Opponent'}
                hasExtraTime={formData.is_cup && formData.our_score_et !== '' && formData.our_score_et !== undefined}
                hasPenalties={formData.is_cup && formData.our_score_pens !== '' && formData.our_score_pens !== undefined}
              />
              
              <Dialog open={addEventOpen} onOpenChange={setAddEventOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-title">Add Match Event</DialogTitle>
                    <DialogDescription className="sr-only">Add a new event to the match</DialogDescription>
                  </DialogHeader>
                  <AddEventForm
                    players={players}
                    lineup={lineup}
                    subs={subs}
                    onAdd={handleAddEvent}
                    onClose={() => setAddEventOpen(false)}
                    formData={formData}
                  />
                </DialogContent>
              </Dialog>
            </>
          )}

          {activeTab === 'stats' && !isNewMatch && (
            <StatsTab
              playerStats={playerStats}
              players={players}
              isViewOnly={isViewOnly}
              onStatChange={handleStatChange}
            />
          )}

          {activeTab === 'dues' && !isNewMatch && (
            <DuesTab
              playerStats={playerStats}
              players={players}
              playerDues={formData.player_dues ? parseFloat(formData.player_dues) : 0}
              isViewOnly={isViewOnly}
              onStatChange={handleStatChange}
            />
          )}

          {activeTab === 'notes' && !isNewMatch && (
            <CoachNotesTab
              notes={formData.coach_notes}
              visibility={formData.coach_notes_visibility}
              readOnly={isViewOnly}
              onChange={(patch) => setFormData({ ...formData, ...patch })}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
